/**
 * Coups de pouce — Attendu 1040 « Déterminer le quotient et le reste dans
 * une division euclidienne » (automatisme 5e).
 *
 * UN SEUL exemple, partagé par les deux fiches : 21 = 8 × 2 + 5.
 *   - son quotient (2) est HORS de l'espace du générateur (q ≥ 3 partout) :
 *     l'aide ne peut jamais souffler un tirage courant : invariant protégé
 *     par cdp1040.self.test.js (arbitrage prof 2026-08-31) ;
 *   - aucun nombre n'y joue deux rôles : 21, 8, 2, 5 et le facteur écarté
 *     (3, celui qui dépasse) sont tous distincts.
 * regle_vf : PAS d'entrée (pur rappel, précédent gen_640) : toute aide utile
 * énoncerait les réponses ; le retour pédagogique vit dans
 * contenu.explications du payload (dormant), affichage = chantier séparé.
 */

export const EXEMPLE_1040 = { a: 21, b: 8, q: 2, r: 5 };

// La droite aux sauts : 2 sauts de 8 mènent à 16, le 3e (grisé) atteindrait
// 24 et dépasse ; 21 est planté entre les deux, le segment ambre porte le
// reste. ⚠ Les valeurs sont celles d'EXEMPLE_1040 : changer l'exemple
// impose de redessiner (le self-test protège le quadruplet, pas le dessin).
const SVG_SAUTS = `
<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 360 150" role="img"
     aria-label="Droite graduée : deux sauts de 8 mènent de 0 à 16, un troisième saut atteindrait 24 et dépasserait 21. Sous l'axe, une flèche mesure l'écart entre 16 et 21 : il manque 5, donc 21 égale 8 fois 2 plus 5.">
  <path d="M20,76 Q72,30 124,76" fill="none" stroke="#10b981" stroke-width="2.5"/>
  <path d="M124,76 Q176,30 228,76" fill="none" stroke="#10b981" stroke-width="2.5"/>
  <path d="M228,76 Q280,30 332,76" fill="none" stroke="#94a3b8" stroke-width="2" stroke-dasharray="5 4"/>
  <text x="72" y="42" font-size="12" fill="#047857" text-anchor="middle" font-weight="bold">+8</text>
  <text x="176" y="42" font-size="12" fill="#047857" text-anchor="middle" font-weight="bold">+8</text>
  <text x="280" y="42" font-size="11" fill="#64748b" text-anchor="middle">dépasse !</text>
  <rect x="228" y="70" width="65" height="12" fill="#fde68a" opacity="0.55" rx="3"/>
  <line x1="16" y1="76" x2="344" y2="76" stroke="#334155" stroke-width="2"/>
  <polygon points="344,76 336,72 336,80" fill="#334155"/>
  <line x1="20" y1="70" x2="20" y2="82" stroke="#334155" stroke-width="2"/>
  <line x1="124" y1="70" x2="124" y2="82" stroke="#334155" stroke-width="2"/>
  <line x1="228" y1="70" x2="228" y2="82" stroke="#334155" stroke-width="2"/>
  <line x1="332" y1="70" x2="332" y2="82" stroke="#94a3b8" stroke-width="2"/>
  <line x1="293" y1="66" x2="293" y2="86" stroke="#e11d48" stroke-width="3"/>
  <text x="20" y="98" font-size="12" fill="#334155" text-anchor="middle">0</text>
  <text x="124" y="98" font-size="12" fill="#334155" text-anchor="middle">8</text>
  <text x="228" y="98" font-size="12" fill="#334155" text-anchor="middle" font-weight="bold">16</text>
  <text x="293" y="98" font-size="13" fill="#e11d48" text-anchor="middle" font-weight="bold">21</text>
  <text x="332" y="98" font-size="12" fill="#94a3b8" text-anchor="middle">24</text>
  <line x1="228" y1="102" x2="228" y2="114" stroke="#d97706" stroke-width="1.5" stroke-dasharray="2 2"/>
  <line x1="293" y1="102" x2="293" y2="114" stroke="#d97706" stroke-width="1.5" stroke-dasharray="2 2"/>
  <line x1="231" y1="118" x2="290" y2="118" stroke="#b45309" stroke-width="2"/>
  <polygon points="228,118 236,114 236,122" fill="#b45309"/>
  <polygon points="293,118 285,114 285,122" fill="#b45309"/>
  <text x="261" y="140" font-size="13" fill="#b45309" text-anchor="middle" font-weight="bold">il manque 5</text>
</svg>`.trim();

// EXEMPLE MODÉLISANT PUR (doctrine 2026-08-31, cf. fiches 1041) : le déroulé
// de la table REMPLACE la règle, les mots des consignes (quotient, reste)
// arrivent APRÈS le geste, ancrés aux nombres.
const FICHE_DIVISION = {
    pages: [
        {
            titre: 'Fais pareil avec tes nombres',
            lignes: [
                `${EXEMPLE_1040.a} divisé par ${EXEMPLE_1040.b} ?`,
                `${EXEMPLE_1040.b} × 1 = ${EXEMPLE_1040.b}`,
                `**${EXEMPLE_1040.b} × ${EXEMPLE_1040.q} = ${EXEMPLE_1040.b * EXEMPLE_1040.q}** ← juste avant ${EXEMPLE_1040.a}`,
                `${EXEMPLE_1040.b} × ${EXEMPLE_1040.q + 1} = ${EXEMPLE_1040.b * (EXEMPLE_1040.q + 1)} ❌ dépasse`,
                `De ${EXEMPLE_1040.b * EXEMPLE_1040.q} à ${EXEMPLE_1040.a}, il manque **${EXEMPLE_1040.r}**.`,
                `Donc **${EXEMPLE_1040.a} = ${EXEMPLE_1040.b} × ${EXEMPLE_1040.q} + ${EXEMPLE_1040.r}** : quotient **${EXEMPLE_1040.q}**, reste **${EXEMPLE_1040.r}**.`,
            ],
        },
        {
            titre: 'En image',
            lignes: [
                `Donc **${EXEMPLE_1040.a} = ${EXEMPLE_1040.b} × ${EXEMPLE_1040.q} + ${EXEMPLE_1040.r}** : quotient **${EXEMPLE_1040.q}**, reste **${EXEMPLE_1040.r}**.`,
            ],
            svg: SVG_SAUTS,
        },
    ],
};

const FICHE_ECRITURE = {
    pages: [
        {
            titre: 'Fais pareil avec tes nombres',
            lignes: [
                `**${EXEMPLE_1040.a} = ${EXEMPLE_1040.b} × ${EXEMPLE_1040.q} + ${EXEMPLE_1040.r}** (${EXEMPLE_1040.a} divisé par ${EXEMPLE_1040.b})`,
                `${EXEMPLE_1040.a} : le **dividende** (le nombre qu'on divise)`,
                `${EXEMPLE_1040.b} : le **diviseur** (le nombre qui divise)`,
                `${EXEMPLE_1040.q} : le **quotient** (le nombre de fois)`,
                `${EXEMPLE_1040.r} : le **reste** (ajouté à la fin)`,
            ],
        },
    ],
};

const CP_1040 = {
    division_texte_trou_saisie: FICHE_DIVISION,
    division_verbale_saisie: FICHE_DIVISION,
    regle_vocabulaire_qcm: FICHE_ECRITURE,
    regle_traduction_qcm: FICHE_ECRITURE,
};

export default CP_1040;
